import { zodResolver } from '@hookform/resolvers/zod';
import { AppError } from '@repo/error';
import { errorMessage, t, validationError } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { Button } from '@repo/ui/button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@repo/ui/form';
import { Textarea } from '@repo/ui/textarea';
import { Trash2Icon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { useSession } from '@/providers/session';
import { trpc } from '@/providers/trpc';
import { useOrganizationStore } from '@/stores/organization-store';
import { dateLabel, QueryState } from './shared';

const schema = z.object({ text: z.string().trim().min(1, t('extra.writeANoteFirst')).max(8000) });
export const Notes = ({
  targetType,
  targetId,
}: {
  targetType: 'report' | 'client';
  targetId: string;
}) => {
  useLocale();

  const { user } = useSession();
  const role = useOrganizationStore((state) => state.currentOrganization.currentOrganizationRole);
  const canManage = ['ORG_OWNER', 'ORG_MANAGER'].includes(role);
  const canWrite = role !== 'ORG_VIEWER';
  const query = trpc.workspace.listNotes.useQuery({ targetType, targetId });
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema, { error: validationError }),
    defaultValues: { text: '' },
  });
  const onError = (cause: unknown) => toast.error(errorMessage(AppError.parse(cause)));
  const create = trpc.workspace.createNote.useMutation({
    onSuccess: () => {
      form.reset();
      void query.refetch();
    },
    onError,
  });
  const remove = trpc.workspace.deleteNote.useMutation({
    onSuccess: () => {
      void query.refetch();
    },
    onError,
  });
  return (
    <section className="raven-section">
      <span className="raven-label">{t('workspace.notes')}</span>
      <h3 className="mb-4">{t('workspace.addYourNote')}</h3>
      <QueryState
        error={query.isError}
        loading={query.isLoading}
        retry={() => void query.refetch()}
      />
      <div className="space-y-3">
        {query.data?.map((note) => (
          <div className="raven-panel flex gap-4" key={note._id}>
            <div className="flex-1">
              <p className="whitespace-pre-wrap text-sm">{note.text}</p>
              <span className="raven-muted">
                {t('workspace.addedBy')} {note.authorName} {t('workspace.on')}{' '}
                {dateLabel(note.createdAt, true)}
              </span>
            </div>
            {(note.authorId === user.id || canManage) && (
              <Button
                aria-label={t('workspace.deleteNote')}
                disabled={remove.isPending}
                onClick={() => remove.mutate({ noteId: note._id })}
                size="icon"
                variant="ghost"
              >
                <Trash2Icon className="size-4" />
              </Button>
            )}
          </div>
        ))}
      </div>
      {canWrite && (
        <Form {...form}>
          <form
            className="mt-4 space-y-3"
            onSubmit={form.handleSubmit(({ text }) =>
              create.mutate({ targetType, targetId, text }),
            )}
          >
            <fieldset disabled={create.isPending}>
              <FormField
                control={form.control}
                name="text"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Textarea
                        {...field}
                        aria-label={t('workspace.newNote')}
                        className="min-h-28 border-0 bg-white"
                        placeholder={t('workspace.addAnything')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="mt-3 flex gap-2">
                <Button type="submit">{t('workspace.save')}</Button>
                <Button onClick={() => form.reset()} type="button" variant="secondary">
                  {t('reports.cancel')}{' '}
                </Button>
              </div>
            </fieldset>
          </form>
        </Form>
      )}
    </section>
  );
};
